import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import testIds from "constants/testIds";
import { useRequestFormReviewValidation } from "@RequestFormV2/context/reviewValidation";
import type { PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import {
  buildCommodityParentPickerOptions,
  projectCommodityRows,
} from "@RequestFormV2/sections/PackingList/utils/commodityProjection";
import { hasCompleteParcelCommodity } from "@RequestFormV2/sections/PackingList/utils/parcelRequirements";
import { canDeleteCommodity } from "@RequestFormV2/sections/PackingList/utils/packingListRowActions";
import { usePackingList } from "@RequestFormV2/sections/PackingList/context";
import AddRowButton from "@RequestFormV2/sections/PackingList/components/shared/AddRowButton";
import PackingListTable from "@RequestFormV2/sections/PackingList/components/shared/PackingListTable";
import ParcelWarningBadge from "@RequestFormV2/sections/PackingList/components/shared/ParcelWarningBadge";
import CommodityTableRow from "@RequestFormV2/sections/PackingList/components/CommoditiesSection/CommodityTableRow";
import { COMMODITY_COLUMN_WIDTHS } from "@RequestFormV2/sections/PackingList/components/shared/packingListColumnWidths";

export default function CommoditiesSection() {
  const intl = useIntl();
  const packingList = useWatch({ name: "packing_list" }) as PackingListRow[] | undefined;
  const { hasAttemptedReview } = useRequestFormReviewValidation();
  const {
    packingListFields,
    handlingUnitHashMap,
    containerTypeOptions,
    appendCommodity,
    isParcelFreight,
    containerRows,
    handlingUnitRows,
  } = usePackingList();

  const commodityRows = useMemo(
    () => projectCommodityRows(packingList, packingListFields),
    [packingList, packingListFields],
  );

  const commodityParentPickerOptions = useMemo(
    () =>
      buildCommodityParentPickerOptions({
        containerRows,
        handlingUnitRows,
        handlingUnitHashMap,
        containerTypeOptions,
        intl,
      }),
    [containerRows, handlingUnitRows, handlingUnitHashMap, containerTypeOptions, intl],
  );

  const hasParentRows = containerRows.length > 0 || handlingUnitRows.length > 0;
  const showParcelWarning = isParcelFreight && !hasCompleteParcelCommodity(packingList);

  const columns = useMemo(
    () => [
      {
        key: "handling_unit_ref",
        header: intl.formatMessage({
          id: "request-form.packing-list.commoditiesAreIn",
          defaultMessage: "Commodities are in",
        }),
        width: COMMODITY_COLUMN_WIDTHS.parentPicker,
      },
      {
        key: "description",
        header: intl.formatMessage({ id: "request-form.packing-list.description", defaultMessage: "Description" }),
        width: COMMODITY_COLUMN_WIDTHS.description,
      },
      {
        key: "hs_code",
        header: intl.formatMessage({ id: "request-form.packing-list.hsCode", defaultMessage: "HS Code" }),
        width: COMMODITY_COLUMN_WIDTHS.hsCode,
      },
      {
        key: "value",
        header: intl.formatMessage({ id: "request-form.packing-list.value", defaultMessage: "Value" }),
        width: COMMODITY_COLUMN_WIDTHS.valueColumn,
      },
    ],
    [intl],
  );

  return (
    <section className="rfv2-packing-list__subsection">
      <div className="rfv2-packing-list__subsection-title">
        {intl.formatMessage({
          id: "request-form.packing-list.commodities",
          defaultMessage: "Commodities information",
        })}
        {showParcelWarning && <ParcelWarningBadge color={hasAttemptedReview ? "error" : "warning"} />}
      </div>

      <PackingListTable columns={columns} scrollable testId={testIds.REQUEST_FORM_PACKING_LIST_COMMODITY_TABLE}>
        {commodityRows.map(({ packingListIndex, commodityIndex, commodityKey, commodity, parentRow }, displayIndex) => (
          // The React key must include the bound form-array indices, not just commodityKey.
          // Commodities are a projected list whose child inputs bind to index-based paths
          // (packing_list.{packingListIndex}.good_customs_information.{commodityIndex}) and are
          // reordered via setValue (duplicate/delete/move). A standalone RHF Controller does not
          // re-sync its displayed value when its `name` shifts under a persisted instance, so a
          // stable-identity-only key would leave a shifted row showing the previous index's value
          // (e.g. duplicating row 1 would bleed its values into a trailing empty row). Encoding the
          // indices forces the shifted rows to remount and re-read the correct path.
          <CommodityTableRow
            key={`${commodityKey}-${packingListIndex}-${commodityIndex}`}
            packingListIndex={packingListIndex}
            commodityIndex={commodityIndex}
            commodityKey={commodityKey}
            commodity={commodity}
            parentRow={parentRow}
            rowNumber={displayIndex + 1}
            deleteDisabled={!canDeleteCommodity(packingList ?? [])}
            commodityParentPickerOptions={commodityParentPickerOptions}
          />
        ))}
      </PackingListTable>

      <AddRowButton
        label={intl.formatMessage({
          id: "request-form.packing-list.addCommodity",
          defaultMessage: "Add commodity",
        })}
        onClick={() => appendCommodity()}
        disabled={!hasParentRows}
        testId={testIds.REQUEST_FORM_PACKING_LIST_ADD_COMMODITY_BUTTON}
      />
    </section>
  );
}
